import type {
  CustomFilterCategory,
  CustomFilterDefinition,
} from '../../api/columns-api-helpers';
import type { DataBuilderCondition } from '../../api/data-builder-api.types';
import {
  type ConditionContext,
  evaluateCondition,
} from '../../api/evaluate-condition';
import type {
  AggregationColumnItem,
  AggregationColumns,
  Breakdown,
  ColumnOption,
  DeliverableColumnConfig,
  UnitOfAnalysisOption,
} from '../../datasets/deliverable-column-config';

/**
 * Store key under which aggregation-column selections live in
 * `columnKeysByBreakdown`. Mirrors `AGGREGATION_BREAKDOWN_ID` in
 * `Step2ColumnsContent` and the `'aggregation'` key the verify-payload builder
 * reads.
 */
export const AGGREGATION_BREAKDOWN_KEY = 'aggregation';

export const buildConditionContext = (
  models: ConditionContext['models'],
  unitOfAnalysis: string | null | undefined,
  settings: ConditionContext['settings']
): ConditionContext => ({
  models,
  settings,
  unitOfAnalysis,
});

export const isConditionVisible = (
  condition: DataBuilderCondition | null | undefined,
  context: ConditionContext
) => evaluateCondition(condition, context);

export const filterColumnOptions = (
  options: ColumnOption[],
  context: ConditionContext
): ColumnOption[] =>
  options.filter((option) => isConditionVisible(option.condition, context));

export const filterUnitOptionsByModels = (
  options: UnitOfAnalysisOption[],
  context: ConditionContext
): UnitOfAnalysisOption[] =>
  options.filter((option) => isConditionVisible(option.condition, context));

export const filterAggregationItemsByModels = (
  items: AggregationColumnItem[],
  context: ConditionContext
): AggregationColumnItem[] => {
  const visibleItems: AggregationColumnItem[] = [];

  for (const item of items) {
    if (item.type === 'column') {
      if (isConditionVisible(item.option.condition, context)) {
        visibleItems.push(item);
      }

      continue;
    }

    if (!isConditionVisible(item.condition, context)) {
      continue;
    }

    const visibleOptions = filterColumnOptions(item.options, context);

    if (visibleOptions.length > 0) {
      visibleItems.push({
        ...item,
        options: visibleOptions,
      });
    }
  }

  return visibleItems;
};

export const isAggregationColumnsVisible = (
  aggregationColumns: AggregationColumns,
  context: ConditionContext
): boolean => isConditionVisible(aggregationColumns.condition, context);

export const filterBreakdownsByModels = (
  breakdowns: Breakdown[],
  context: ConditionContext
): Breakdown[] =>
  breakdowns.flatMap((breakdown) => {
    if (!isConditionVisible(breakdown.condition, context)) {
      return [];
    }

    return [
      {
        ...breakdown,
        columnOptions: filterColumnOptions(breakdown.columnOptions, context),
        columnItems: filterAggregationItemsByModels(
          breakdown.columnItems,
          context
        ),
      },
    ];
  });

export const filterCustomFilterDefinitionsByCondition = (
  defs: CustomFilterDefinition[],
  context: ConditionContext
): CustomFilterDefinition[] => {
  const filteredDefs = defs.filter((def) =>
    isConditionVisible(def.condition, context)
  );
  return filteredDefs;
};

export const filterCustomFilterCategoriesByCondition = (
  categories: CustomFilterCategory[],
  context: ConditionContext
): CustomFilterCategory[] => {
  const out: CustomFilterCategory[] = [];
  for (const category of categories) {
    if (!isConditionVisible(category.condition, context)) {
      continue;
    }
    const visibleFilters = filterCustomFilterDefinitionsByCondition(
      category.filters,
      context
    );
    if (visibleFilters.length === 0) {
      continue;
    }
    out.push({ ...category, filters: visibleFilters });
  }
  return out;
};

export const getVisibleColumnKeys = (
  items: AggregationColumnItem[]
): Set<string> => {
  const keys = new Set<string>();

  for (const item of items) {
    if (item.type === 'column') {
      keys.add(item.option.key);
      continue;
    }

    for (const option of item.options) {
      keys.add(option.key);
    }
  }

  return keys;
};

export const collectRequiredKeysFromAggregationItems = (
  items: AggregationColumnItem[]
): string[] => {
  const keys: string[] = [];
  for (const item of items) {
    if (item.type === 'column' && item.option.isRequired) {
      keys.push(item.option.key);
      continue;
    }
    if (item.type === 'group') {
      for (const option of item.options) {
        if (option.isRequired) {
          keys.push(option.key);
        }
      }
    }
  }
  return keys;
};

export const collectDefaultKeysFromAggregationItems = (
  items: AggregationColumnItem[]
): string[] => {
  const keys: string[] = [];
  for (const item of items) {
    if (item.type === 'column' && item.option.isDefault) {
      keys.push(item.option.key);
      continue;
    }
    if (item.type === 'group') {
      for (const option of item.options) {
        if (option.isDefault) {
          keys.push(option.key);
        }
      }
    }
  }
  return keys;
};

/**
 * Visible column keys per breakdown (plus the aggregation section under
 * {@link AGGREGATION_BREAKDOWN_KEY}) for a given condition context. A breakdown
 * whose own condition fails — or the aggregation section when hidden —
 * contributes an empty array so callers can prune away every selected key.
 *
 * Used to reconcile `columnKeysByBreakdown` atomically when a setting changes,
 * so the state never holds a now-invisible column alongside the new setting
 * value (which would otherwise leak into the `/estimate` and submit payloads).
 */
export const getVisibleColumnKeysByBreakdown = (
  config: Pick<DeliverableColumnConfig, 'aggregationColumns' | 'breakdowns'>,
  context: ConditionContext
): Record<string, string[]> => {
  const result: Record<string, string[]> = {
    [AGGREGATION_BREAKDOWN_KEY]: isAggregationColumnsVisible(
      config.aggregationColumns,
      context
    )
      ? [
          ...getVisibleColumnKeys(
            filterAggregationItemsByModels(
              config.aggregationColumns.items,
              context
            )
          ),
        ]
      : [],
  };

  for (const breakdown of config.breakdowns) {
    result[breakdown.id] = isConditionVisible(breakdown.condition, context)
      ? [
          ...getVisibleColumnKeys(
            filterAggregationItemsByModels(breakdown.columnItems, context)
          ),
        ]
      : [];
  }

  return result;
};

export const mergeVisibleSelectedWithRequiredKeys = (
  selected: string[],
  visibleKeys: Set<string>,
  requiredKeys: string[],
  newlyVisibleDefaultKeys: string[] = []
): string[] => {
  const requiredVisible = requiredKeys.filter((k) => visibleKeys.has(k));
  const preserved = selected.filter((k) => visibleKeys.has(k));
  return [
    ...new Set([...preserved, ...requiredVisible, ...newlyVisibleDefaultKeys]),
  ];
};
